import { useState, useMemo, useEffect, useCallback } from 'react'
import { Box } from '@mui/material'
import DuplicateIcon from 'assets/icons/icon-image-control-point-duplicate-24-px.svg?react'
import DeleteIcon from 'assets/icons/icon-delete.svg?react'
import RemoveIcon from '@mui/icons-material/Remove'
import { useIntl } from 'react-intl'
import { ContextMenuItem } from 'features/common/ui/ContextMenu'
import HeaderContextMenuIcon from 'features/common/ui/AppTemplate/Header/Partials/HeaderContextMenuIcon'

import PartiallyDuplicateMapDialog from 'features/common/ui/Map/PartiallyDuplicateMapDialog'
import { useAppSelector } from 'app/state'
import { dryRunDuplicateMapActionDefinition } from 'features/maps/state/duplicateMapSlice'

import useDuplicateMap from 'features/maps/state/useDuplicateMap'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import MapsHardQuotaModal from 'features/maps/ui/MapsHardQuotaModal'
import { useReduxAction } from 'app/state/action'
import { isHardQuotaError } from 'features/quotas/utils'
import { QuotaUxTrigger } from 'features/hubspot/types'
import { useNavigate } from 'react-router-dom'
import { useViewerMapCredentials } from 'features/connections/hooks/useViewerMapCredentials'
import DeleteCredentialsDialog from 'features/connections/ui/ViewerCredentials/DeleteCredentialsDialog'
import useMapSharingInfo from 'features/maps/state/useMapSharingInfo'
import useUserRoles from 'features/account/state/useUserRoles'
import { createDialogOpener } from 'features/dialogs/openDialog'

const openMapDeleteDialog = createDialogOpener(() => import('features/maps/ui/MapCard/MapDeleteDialog'))

enum MapActions {
  Duplicate = 'duplicate',
  Delete = 'delete',
  RemoveCredentials = 'removeCredentials'
}

function BuilderHeaderActionsMenu({ viewOnly }: { viewOnly?: boolean }) {
  const intl = useIntl()
  const currentMap = useAppSelector((state) => state.maps.currentMap)
  const currentMapId = currentMap?.id
  const currentMapDatasets = useAppSelector((state) => state.maps.currentMapDatasets)

  const { isMapOwner } = useMapSharingInfo(currentMap)
  const { userIsViewer } = useUserRoles()

  const noDatasets = currentMapDatasets.length === 0
  const disableDuplicate = !currentMap?.title && noDatasets

  const { duplicateMapConflicts } = useDuplicateMap()
  const { someCredentialsAttached, connectionsWithCredentialsAttached } = useViewerMapCredentials()
  const navigate = useNavigate()

  const [isDuplicateMapOpen, setIsDuplicateMapOpen] = useState(false)
  const [isRemoveCredentialsOpen, setIsRemoveCredentialsOpen] = useState(false)

  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota)
  const dryRunDuplicateMapAction = useReduxAction(dryRunDuplicateMapActionDefinition)

  useEffect(() => {
    duplicateMapConflicts && setIsDuplicateMapOpen(true)
  }, [duplicateMapConflicts])

  const actions = useMemo(
    () => [
      {
        hidden: userIsViewer,
        id: MapActions.Duplicate,
        label: intl.formatMessage({ id: 'builder.actions.duplicate.title' }),
        startAdornment: <DuplicateIcon />,
        disabled: disableDuplicate,
        tooltipId: disableDuplicate ? 'builder.actions.duplicate.disabledTooltip' : undefined
      },
      {
        hidden: someCredentialsAttached === false,
        id: MapActions.RemoveCredentials,
        label: intl.formatMessage({ id: 'builder.actions.credentials.remove' }),
        startAdornment: <RemoveIcon />
      },
      {
        hidden: viewOnly || !isMapOwner,
        id: MapActions.Delete,
        label: intl.formatMessage({ id: 'builder.actions.delete.title' }),
        startAdornment: <DeleteIcon />,
        destructive: true,
        divider: true
      }
    ],
    [intl, userIsViewer, disableDuplicate, someCredentialsAttached, viewOnly, isMapOwner]
  )

  const handleDeleted = () => {
    navigate('/maps')
  }

  const handleClickOption = (item: ContextMenuItem) => {
    if (item.id === MapActions.Duplicate && currentMapId) {
      if (hardQuotaModalController.additionWouldExceedQuota()) {
        hardQuotaModalController.open()
        return
      }
      dryRunDuplicateMapAction.invoke(currentMapId)
    } else if (item.id === MapActions.Delete) {
      if (!currentMap) return

      openMapDeleteDialog({ map: currentMap, onDeleted: handleDeleted })
    } else if (item.id === MapActions.RemoveCredentials) {
      setIsRemoveCredentialsOpen(true)
    }
  }

  const handleDuplicateClose = () => {
    setIsDuplicateMapOpen(false)
  }

  const handleRemoveCredentialsClose = () => {
    setIsRemoveCredentialsOpen(false)
  }

  const handleCloseHardQuotaModal = useCallback(() => {
    hardQuotaModalController.props.onCloseModal()
    dryRunDuplicateMapAction.dismissResult()
  }, [dryRunDuplicateMapAction, hardQuotaModalController.props])

  useEffect(() => {
    if (isHardQuotaError(dryRunDuplicateMapAction.error)) {
      hardQuotaModalController.open()
    }
  })

  const atLeastOneActionIsVisible = actions.some((action) => !action.hidden)

  return (
    <>
      {atLeastOneActionIsVisible && (
        <Box>
          <HeaderContextMenuIcon aria-label="builder-actions" menuItems={actions} onClickItem={handleClickOption} />
        </Box>
      )}
      {currentMapId && (
        <PartiallyDuplicateMapDialog mapId={currentMapId} isOpen={isDuplicateMapOpen} onClose={handleDuplicateClose} />
      )}
      {currentMap && someCredentialsAttached && (
        <DeleteCredentialsDialog
          connections={connectionsWithCredentialsAttached || []}
          onClose={handleRemoveCredentialsClose}
          open={isRemoveCredentialsOpen}
        />
      )}
      <MapsHardQuotaModal
        {...hardQuotaModalController.props}
        onCloseModal={handleCloseHardQuotaModal}
        variant="duplicate"
        quotaUxTrigger={QuotaUxTrigger.DuplicateMap}
      />
    </>
  )
}

export default BuilderHeaderActionsMenu
